import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  History, 
  Download, 
  Filter,
  Power,
  Gauge,
  Calendar,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogsPage = () => {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    device_id: '',
    start_date: '',
    end_date: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, devicesRes] = await Promise.all([
        axios.get(`${API}/logs`, { params: filters }),
        axios.get(`${API}/devices`)
      ]);
      setLogs(logsRes.data);
      setDevices(devicesRes.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilter = () => {
    fetchData();
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/logs/export`, { params: filters });
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export complete / 匯出完成');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'open':
      case 'close':
        return <Power className="w-4 h-4" />;
      case 'set_pressure':
        return <Gauge className="w-4 h-4" />;
      case 'schedule_created':
        return <Calendar className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'open':
        return 'text-fac-green';
      case 'close':
        return 'text-fac-red';
      case 'set_pressure':
        return 'text-fac-blue';
      default:
        return 'text-fac-yellow';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-fac-dark pt-16 pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h1 className="font-heading font-bold text-3xl text-white uppercase" data-testid="logs-title">
            {t('logs_title')}
            <span className="block text-fac-green font-chinese text-xl mt-1">活動紀錄</span>
          </h1>
          
          <Button
            onClick={handleExport}
            className="bg-fac-green text-black font-bold"
            data-testid="export-logs-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('logs_export')}
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-fac-card border-border mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {t('logs_filter')} / 篩選
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select
                value={filters.device_id}
                onValueChange={(value) => setFilters({ ...filters, device_id: value })}
              >
                <SelectTrigger className="bg-fac-dark border-border text-white" data-testid="filter-device-select">
                  <SelectValue placeholder="All Devices / 所有設備" />
                </SelectTrigger>
                <SelectContent className="bg-fac-card border-border">
                  <SelectItem value="">All Devices / 所有設備</SelectItem>
                  {devices.map((device) => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="bg-fac-dark border-border text-white"
                placeholder="Start Date"
                data-testid="filter-start-date"
              />

              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="bg-fac-dark border-border text-white"
                placeholder="End Date"
                data-testid="filter-end-date"
              />

              <Button
                onClick={handleFilter}
                className="bg-fac-blue text-black font-bold"
                data-testid="apply-filter-btn"
              >
                Apply / 套用
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs List */}
        <Card className="bg-fac-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <History className="w-5 h-5" />
              History / 歷史紀錄
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-fac-paper rounded-lg">
                    <div className="w-8 h-8 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-1/2" />
                      <div className="h-3 bg-muted rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : logs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 font-chinese">
                {t('logs_empty')}
              </p>
            ) : (
              <div className="space-y-3" data-testid="logs-list">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 p-4 bg-fac-paper rounded-lg border border-border"
                    data-testid={`log-${log.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-fac-card flex items-center justify-center ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">
                        {log.details}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 font-chinese">
                        {formatDate(log.created_at)}
                      </p>
                    </div>
                    <span className={`text-xs font-mono uppercase px-2 py-1 rounded ${getActionColor(log.action)} bg-fac-card`}>
                      {log.action}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LogsPage;
